import { STABLE_IDS } from './isGlobalSystemFieldStableId.tsx';
import type {
	FieldType,
	FieldTypeMap,
	GlobalFieldType,
	GlobalFieldTypeMap,
	GlobalSystemFieldTypeMap,
	JiraApiFieldTypeMap,
	SharedFieldTypeMap,
	StandardFieldType,
} from './FieldType.types.tsx';

export const POLARIS_SUB_TYPES = {
	ARCHIVED: 'ARCHIVED',
	ARCHIVED_BY: 'ARCHIVED_BY',
	ARCHIVED_ON: 'ARCHIVED_ON',
	DELIVERY_PROGRESS: 'DELIVERY_PROGRESS',
	DELIVERY_STATUS: 'DELIVERY_STATUS',
	ISSUE_COMMENTS: 'ISSUE_COMMENTS',
	LINKED_ISSUES: 'LINKED_ISSUES',
	NUM_DATA_POINTS: 'NUM_DATA_POINTS',
	ATLAS_GOAL: 'ATLAS_GOAL',
	ATLAS_PROJECT: 'ATLAS_PROJECT',
	ATLAS_PROJECT_STATUS: 'ATLAS_PROJECT_STATUS',
} as const;

// builtin
const ISSUE_KEY_FIELD_TYPE = 'key'; // issuekey also works.
const DESCRIPTION_FIELD_TYPE = 'description';
const ASSIGNEE_FIELD_TYPE = 'assignee';
const CREATED_FIELD_TYPE = 'created';
const CREATOR_FIELD_TYPE = 'creator';
const STATUS_FIELD_TYPE = 'status';
const SUMMARY_FIELD_TYPE = 'summary';
const UPDATED_FIELD_TYPE = 'updated';
const LABELS_FIELD_TYPE = 'labels';
const ISSUE_TYPE_FIELD_TYPE = 'issuetype';
const REPORTER_FIELD_TYPE = 'reporter';
const ISSUE_LINKS_FIELD_TYPE = 'issuelinks';
const PRIORITY_FIELD_TYPE = 'priority';
const PROJECT_FIELD_TYPE = 'project';
const FIX_VERSIONS_FIELD_TYPE = 'fixVersions';

// jira custom field types
const SHORT_TEXT_FIELD_TYPE = 'com.atlassian.jira.plugin.system.customfieldtypes:textfield';
const NUMBER_FIELD_TYPE = 'com.atlassian.jira.plugin.system.customfieldtypes:float';
const DATE_FIELD_TYPE = 'com.atlassian.jira.plugin.system.customfieldtypes:datepicker';
const HYPERLINK_FIELD_TYPE = 'com.atlassian.jira.plugin.system.customfieldtypes:url';
const CUSTOM_LABELS_FIELD_TYPE = 'com.atlassian.jira.plugin.system.customfieldtypes:labels';
const SINGLE_SELECT_FIELD_TYPE = 'com.atlassian.jira.plugin.system.customfieldtypes:select';
const MULTI_SELECT_FIELD_TYPE = 'com.atlassian.jira.plugin.system.customfieldtypes:multicheckboxes';
const JSW_MULTI_SELECT_FIELD_TYPE = 'com.atlassian.jira.plugin.system.customfieldtypes:multiselect';
const PEOPLE_FIELD_TYPE = 'com.atlassian.jira.plugin.system.customfieldtypes:people';
const JSW_PEOPLE_FIELD_TYPE = 'com.atlassian.jira.plugin.system.customfieldtypes:multiuserpicker';
const CF_DEVELOPMENT_FIELD_TYPE =
	'com.atlassian.jira.plugins.jira-development-integration-plugin:devsummarycf';
const TEAM_FIELD_TYPE = 'com.atlassian.jira.plugin.system.customfieldtypes:atlassian-team';
const PLATFORM_GOALS_FIELD_TYPE = 'com.atlassian.jira.plugin.system.customfieldtypes:goals';
const PLATFORM_FOCUS_AREAS_FIELD_TYPE =
	'com.atlassian.jira.plugin.system.customfieldtypes:focus-areas';
const PLATFORM_ATLAS_PROJECT_FIELD_TYPE =
	'com.atlassian.jira.plugin.system.customfieldtypes:atlas-project';

// jira api only field types
const EXTERNAL_REFERENCE_FIELD_TYPE = 'jira.polaris:external-reference';
const DATE_TIME_FIELD_TYPE = 'com.atlassian.jira.plugin.system.customfieldtypes:datetime';

// polaris only types
const RATING_FIELD_TYPE = 'jira.polaris:rating';
const SLIDER_FIELD_TYPE = 'jira.polaris:slider';
const FORMULA_FIELD_TYPE = 'jira.polaris:formula';
const CHECKBOX_FIELD_TYPE = 'jira.polaris:boolean';
const INTERVAL_FIELD_TYPE = 'jira.polaris:interval';
const REACTIONS_FIELD_TYPE = 'jira.polaris:reactions';
const VOTES_FIELD_TYPE = 'jira.polaris:votes';
const CONNECTION_FIELD_TYPE = 'jira.polaris:connection';

// polaris only global system types
const INSIGHT_IMPACT_FIELD_TYPE = 'jira.polaris:insight-impact';
const INSIGHTS_GLOBAL_FIELD_TYPE = 'jira.polaris:count-insights';
const LINKED_ISSUES_GLOBAL_FIELD_TYPE = 'jira.polaris:count-linked-issues';
const DELIVERY_PROGRESS_GLOBAL_FIELD_TYPE = 'jira.polaris:delivery-progress';
const DELIVERY_STATUS_GLOBAL_FIELD_TYPE = 'jira.polaris:delivery-status';
const PLATFORM_PROJECT_FIELD_TYPE = 'jira.polaris:atlassian-project';
const PLATFORM_PROJECT_STATUS_FIELD_TYPE = 'jira.polaris:atlassian-project-status';
const ISSUE_COMMENTS_GLOBAL_FIELD_TYPE = 'jira.polaris:count-issue-comments';

// Virtual Fields
const ISSUE_ID_FIELD_TYPE = 'issueid';

// Frontend only: Jira missing type (formula, external-reference, archive...).
const ATLAS_PROJECT_FIELD_TYPE = 'atlasProject';
const ATLAS_GOAL_FIELD_TYPE = 'atlasGoal';
const ATLAS_PROJECT_STATUS_FIELD_TYPE = 'atlasProjectStatus';
const DELIVERY_PROGRESS_FIELD_TYPE = 'deliveryProgress';
const LINKED_ISSUES_FIELD_TYPE = 'linkedIssues';
const ARCHIVED_BY_FIELD_TYPE = 'archivedBy';
const ARCHIVED_ON_FIELD_TYPE = 'archivedOn';
const ARCHIVED_FIELD_TYPE = 'archived';
const ISSUE_COMMENTS_FIELD_TYPE = 'issueComments'; // issue comments count
const INSIGHTS_FIELD_TYPE = 'numDataPoints';
const DELIVERY_STATUS_FIELD_TYPE = 'deliveryStatus';

// others
const RANK_FIELD_TYPE = 'com.pyxis.greenhopper.jira:gh-lexo-rank';
const SPRINT_FIELD_TYPE = 'com.pyxis.greenhopper.jira:gh-sprint';

export const GLOBAL_SYSTEM_FIELD_TYPES: GlobalSystemFieldTypeMap = {
	PLATFORM_GOALS: PLATFORM_GOALS_FIELD_TYPE,
	PLATFORM_FOCUS_AREAS: PLATFORM_FOCUS_AREAS_FIELD_TYPE,
	INSIGHTS_GLOBAL: INSIGHTS_GLOBAL_FIELD_TYPE,
	LINKED_ISSUES_GLOBAL: LINKED_ISSUES_GLOBAL_FIELD_TYPE,
	DELIVERY_PROGRESS_GLOBAL: DELIVERY_PROGRESS_GLOBAL_FIELD_TYPE,
	DELIVERY_STATUS_GLOBAL: DELIVERY_STATUS_GLOBAL_FIELD_TYPE,
	ISSUE_COMMENTS_GLOBAL: ISSUE_COMMENTS_GLOBAL_FIELD_TYPE,
	PLATFORM_PROJECT: PLATFORM_PROJECT_FIELD_TYPE,
	PLATFORM_PROJECT_STATUS: PLATFORM_PROJECT_STATUS_FIELD_TYPE,
	PLATFORM_ATLAS_PROJECT: PLATFORM_ATLAS_PROJECT_FIELD_TYPE,
};

export const GLOBAL_FIELD_TYPES: GlobalFieldTypeMap = {
	...GLOBAL_SYSTEM_FIELD_TYPES,
	CONNECTION: CONNECTION_FIELD_TYPE,
	CHECKBOX: CHECKBOX_FIELD_TYPE,
	FORMULA: FORMULA_FIELD_TYPE,
	INTERVAL: INTERVAL_FIELD_TYPE,
	HYPERLINK: HYPERLINK_FIELD_TYPE,
	MULTI_SELECT: MULTI_SELECT_FIELD_TYPE,
	JSW_MULTI_SELECT: JSW_MULTI_SELECT_FIELD_TYPE,
	NUMBER: NUMBER_FIELD_TYPE,
	RATING: RATING_FIELD_TYPE,
	REACTIONS: REACTIONS_FIELD_TYPE,
	SHORT_TEXT: SHORT_TEXT_FIELD_TYPE,
	SINGLE_SELECT: SINGLE_SELECT_FIELD_TYPE,
	SLIDER: SLIDER_FIELD_TYPE,
	JSW_PEOPLE: JSW_PEOPLE_FIELD_TYPE,
	TEAM: TEAM_FIELD_TYPE,
};

const SHARED_FIELD_TYPES: SharedFieldTypeMap = {
	...GLOBAL_FIELD_TYPES,
	ASSIGNEE: ASSIGNEE_FIELD_TYPE,
	CF_DEVELOPMENT: CF_DEVELOPMENT_FIELD_TYPE,
	CREATED: CREATED_FIELD_TYPE,
	CREATOR: CREATOR_FIELD_TYPE,
	CUSTOM_LABELS: CUSTOM_LABELS_FIELD_TYPE,
	DATE: DATE_FIELD_TYPE,
	DESCRIPTION: DESCRIPTION_FIELD_TYPE,
	ISSUE_ID: ISSUE_ID_FIELD_TYPE,
	ISSUE_KEY: ISSUE_KEY_FIELD_TYPE,
	ISSUE_LINKS: ISSUE_LINKS_FIELD_TYPE,
	ISSUE_TYPE: ISSUE_TYPE_FIELD_TYPE,
	LABELS: LABELS_FIELD_TYPE,
	PEOPLE: PEOPLE_FIELD_TYPE,
	PRIORITY: PRIORITY_FIELD_TYPE,
	PROJECT: PROJECT_FIELD_TYPE,
	RANK: RANK_FIELD_TYPE,
	REPORTER: REPORTER_FIELD_TYPE,
	STATUS: STATUS_FIELD_TYPE,
	SUMMARY: SUMMARY_FIELD_TYPE,
	UPDATED: UPDATED_FIELD_TYPE,
	VOTES: VOTES_FIELD_TYPE,
	CONNECTION: CONNECTION_FIELD_TYPE,
	FIX_VERSIONS: FIX_VERSIONS_FIELD_TYPE,
	SPRINT: SPRINT_FIELD_TYPE,
};

// types comming from jira API
export const JIRA_API_FIELD_TYPES: JiraApiFieldTypeMap = {
	...SHARED_FIELD_TYPES,
	EXTERNAL_REFERENCE: EXTERNAL_REFERENCE_FIELD_TYPE,
	DATE_TIME: DATE_TIME_FIELD_TYPE,
};

// types used on the Frontend
export const FIELD_TYPES: FieldTypeMap = {
	...SHARED_FIELD_TYPES,
	ARCHIVED_BY: ARCHIVED_BY_FIELD_TYPE,
	ARCHIVED_ON: ARCHIVED_ON_FIELD_TYPE,
	ARCHIVED: ARCHIVED_FIELD_TYPE,
	ATLAS_GOAL: ATLAS_GOAL_FIELD_TYPE,
	ATLAS_PROJECT_STATUS: ATLAS_PROJECT_STATUS_FIELD_TYPE,
	ATLAS_PROJECT: ATLAS_PROJECT_FIELD_TYPE,
	DELIVERY_PROGRESS: DELIVERY_PROGRESS_FIELD_TYPE,
	DELIVERY_STATUS: DELIVERY_STATUS_FIELD_TYPE,
	INSIGHT_IMPACT: INSIGHT_IMPACT_FIELD_TYPE,
	INSIGHTS: INSIGHTS_FIELD_TYPE,
	ISSUE_COMMENTS: ISSUE_COMMENTS_FIELD_TYPE,
	LINKED_ISSUES: LINKED_ISSUES_FIELD_TYPE,
	CONNECTION: CONNECTION_FIELD_TYPE,
};

export const FIELD_TYPES_CATEGORIES: {
	ARCHIVED: FieldType[];
	ATLAS: FieldType[];
	USER: FieldType[];
} = {
	ARCHIVED: [FIELD_TYPES.ARCHIVED_BY, FIELD_TYPES.ARCHIVED, FIELD_TYPES.ARCHIVED_ON],
	ATLAS: [
		FIELD_TYPES.ATLAS_GOAL,
		FIELD_TYPES.ATLAS_PROJECT,
		FIELD_TYPES.ATLAS_PROJECT_STATUS,
		FIELD_TYPES.PLATFORM_GOALS,
		FIELD_TYPES.PLATFORM_FOCUS_AREAS,
		FIELD_TYPES.PLATFORM_PROJECT,
		FIELD_TYPES.PLATFORM_PROJECT_STATUS,
	],
	USER: [FIELD_TYPES.ASSIGNEE, FIELD_TYPES.CREATOR, FIELD_TYPES.REPORTER],
};

const standardFieldTypeList = [
	FIELD_TYPES.CHECKBOX,
	FIELD_TYPES.INTERVAL,
	FIELD_TYPES.HYPERLINK,
	FIELD_TYPES.MULTI_SELECT,
	FIELD_TYPES.NUMBER,
	FIELD_TYPES.RATING,
	FIELD_TYPES.REACTIONS,
	FIELD_TYPES.SHORT_TEXT,
	FIELD_TYPES.SINGLE_SELECT,
	FIELD_TYPES.SLIDER,
];

// This list contain standard field types that could be created (does not include legacy field types)
// without connection as it is added conditionally to the list
export const STANDARD_SPACE_NEW_FIELD_TYPE_LIST: StandardFieldType[] = [
	...standardFieldTypeList,
	FIELD_TYPES.PEOPLE,
];

export const STANDARD_GLOBAL_FIELD_TYPE_LIST: GlobalFieldType[] = [
	...standardFieldTypeList,
	FIELD_TYPES.JSW_PEOPLE,
];

export const UNSUPPORTED_STABLE_IDS_FOR_PINNING: 'sfid:jira-software:start-date'[] = [
	STABLE_IDS.START_DATE,
];

export const fieldTypes: FieldTypeMap = FIELD_TYPES;
